/* eslint-disable no-nested-ternary */
import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { offsetDate } from '@mummssoftware/utils/orders/orders-utils';
import { FormControl } from '@material-ui/core';
import { formatutcPrintMMDDYY } from '@mummssoftware/utils/formatters';
import {
  findMaxEditableDate,
  findMinEditableDate,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import {
  VFGlobalContext,
  ADD_EDITING_DATA,
} from '@mummssoftware/global-context/VFGlobalContext';
import {
  GlobalContext,
  OrdersGlobalContext,
} from '@mummssoftware/global-context';
import { ReactDatePicker } from '@mummssoftware/common-ui';
import { CalendarDiv } from '../layout/WrappedDivs';
import { CarePlanFrequencyContext } from '../../context';
import { CPFContext } from '../../containers/Context/CPFContext';
import {
  getStagedOrderEntry,
  buildOrderEntryPayload,
  resolveCarePlanForRow,
} from './orderEntryHelpers';
import {
  getWidgetEditMode,
  getCellEditRights,
  CPF_EDIT_MODE,
  CPF_SAVE_VIA,
  CpfEditMode,
} from '../../editPolicy';
import { Effective } from '../../utils/constant';
import {
  resolveInlineEffectiveSeed,
  resolveInlineMinEffectiveDate,
} from './cellEditState';

export const EditEffectiveCell = React.memo((props: any) => {
  const { data, rgHash, isEditable, editMode: editModeProp } = props;
  const { roleGroupId, name, orderEntryId } = data;
  const { patient, lastIdgInformation, readOnly, currentUser } = useContext(
    CarePlanFrequencyContext,
  );
  const { VFordersMode, dispatchVF, VForderData, VFOrderDate } = useContext(
    VFGlobalContext,
  );
  const { patientDateRestrictions } = useContext(GlobalContext);
  const { orderType } = useContext(OrdersGlobalContext);

  const basicDateConstraint = patientDateRestrictions?.basicDateConstraint;
  const { editingRoleGroupId, isCPFAgency, isPRNOrderAgency } = useContext(
    CPFContext,
  );

  const mode: CpfEditMode =
    editModeProp ||
    getWidgetEditMode({
      isCPFAgency: !!isCPFAgency,
      isPRNOrderAgency: !!isPRNOrderAgency,
      VFordersMode,
    });

  // Order dialog: isEditable / editMode ORDER. Grid inline: this row locked.
  const isActiveRow =
    !!isEditable ||
    editModeProp === CPF_EDIT_MODE.ORDER ||
    (editingRoleGroupId != null &&
      String(editingRoleGroupId) === String(roleGroupId));

  const stagedEntry = getStagedOrderEntry(
    data,
    VForderData,
    VFordersMode,
    roleGroupId,
  );
  const editing = stagedEntry;

  const carePlan =
    resolveCarePlanForRow(data, rgHash) ||
    rgHash[roleGroupId?.toString()]?.frequencies?.dummy;

  const rights = getCellEditRights({
    mode,
    column: Effective,
    isActiveRow,
    carePlan,
    readOnly,
  });
  const { show: displayEdit, write: allowWrite } = rights;

  // Order dialog creates a new frequency → floor on the current one.
  // Inline edits correct this row → floor on prior history, not itself.
  const minimumDate: string | null =
    (VFordersMode && VFOrderDate) ||
    (mode === CPF_EDIT_MODE.ORDER
      ? findMinEditableDate(basicDateConstraint, carePlan?.effectiveDate)
      : resolveInlineMinEffectiveDate(basicDateConstraint, carePlan));

  const includeTwoWeeks = false;
  const maxDate = new Date(
    findMaxEditableDate(patient, lastIdgInformation, includeTwoWeeks),
  );

  const [effDate, setDate] = useState(
    VFordersMode && editing
      ? editing.effectiveDate || null
      : isEditable
      ? null
      : carePlan?.effectiveDate
      ? new Date(carePlan.effectiveDate)
      : null,
  );

  useEffect(() => {
    if (!allowWrite || !carePlan) return;
    if (mode === CPF_EDIT_MODE.ORDER) {
      carePlan.editEffective = effDate;
      return;
    }
    // Prefer editEffective (failed-save remount) then saved effectiveDate.
    const seeded = resolveInlineEffectiveSeed(carePlan);
    if (seeded) {
      carePlan.editEffective = seeded;
      setDate(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowWrite, mode, carePlan?.id]);

  useEffect(() => {
    if (mode !== CPF_EDIT_MODE.ORDER && stagedEntry?.effectiveDate) {
      setDate(stagedEntry.effectiveDate);
    }
  }, [stagedEntry?.effectiveDate, mode]);

  const handleEffectiveDateChange = (date: Date) => {
    if (!allowWrite) return;
    const newDate = offsetDate(date);
    if (carePlan) carePlan.editEffective = newDate;
    setDate(newDate);
    if (rights.saveVia === CPF_SAVE_VIA.ORDER) {
      dispatchVF({
        type: ADD_EDITING_DATA,
        payload: buildOrderEntryPayload(
          { roleGroupId, name, orderEntryId },
          { effectiveDate: newDate, orderType },
        ),
      });
    }
  };

  const siteTimezone = currentUser?.siteTimezone || moment.tz.guess();
  const formatEffectiveDate = (date: string | Date) =>
    formatutcPrintMMDDYY(
      date instanceof Date ? date.toISOString() : date,
      siteTimezone,
    );

  return displayEdit ? (
    <FormControl component="fieldset">
      <CalendarDiv>
        <ReactDatePicker
          portalId={mode === CPF_EDIT_MODE.ORDER ? undefined : 'root'}
          inputTextAlign="unset"
          popperPosition="fixed"
          selectedDate={effDate ? new Date(effDate) : null}
          onChange={handleEffectiveDateChange}
          minimumDate={moment(minimumDate).toDate()}
          maximumDate={maxDate}
        />
      </CalendarDiv>
    </FormControl>
  ) : stagedEntry?.effectiveDate || data.stagedEffectiveDate ? (
    <div>
      {formatEffectiveDate(
        stagedEntry?.effectiveDate || data.stagedEffectiveDate,
      )}
    </div>
  ) : (
    <div>
      {carePlan?.effectiveDate
        ? formatEffectiveDate(carePlan.effectiveDate)
        : ''}
    </div>
  );
});

export default EditEffectiveCell;
